<template>
  <div
    class="nav-desktop-subdrop"
    :class="{ 'nav-desktop-subdrop--open': open }"
  >
    <div class="nav-desktop-subdrop__content-wrapper">
      <div
        class="nav-desktop-subdrop__content"
        :class="[
          { 'nav-desktop-subdrop__content--overview': withLinkOverview },
          { 'nav-desktop-subdrop__content--media': withLinkMedia },
        ]"
      >
        <div
          v-if="withLinkOverview"
          class="nav-desktop-subdrop__overview"
        >
          <T3Link
            :to="rootLink.link"
            class="nav-desktop-subdrop__overview-header"
          >
            <span @click="handleLinkClick">
              <T3HtmlParser
                :content="rootLink.header"
                tag="span"
              /> <SvgIconLinkArrow />
            </span>
          </T3Link>

          <T3HtmlParser
            :content="rootLink.description"
            class="nav-desktop-subdrop__overview-description"
          />
        </div>

        <ul class="nav-desktop-subdrop__list">
          <li
            v-for="(link, index) in rootLink.children"
            :key="index"
          >
            <template v-if="link.children?.length">
              <component
                :is="link.spacer ? 'div' : 't3-link'"
                :to="link.spacer ? null : link.link"
                class="nav-desktop-subdrop__sublist-title"
              >
                <T3HtmlParser :content="link.title" />
                <SvgIconLinkArrow v-if="!link.spacer" />
              </component>

              <ul class="nav-desktop-subdrop__sublist">
                <li
                  v-for="(sublink, subindex) in link.children"
                  :key="subindex"
                >
                  <T3Link
                    :to="getLink(sublink)"
                    class="nav-desktop-subdrop__link"
                  >
                    <span @click="handleLinkClick">
                      {{ sublink.title }}
                    </span>
                  </T3Link>
                </li>
              </ul>
            </template>

            <template v-else>
              <T3Link
                :to="getLink(link)"
                class="nav-desktop-subdrop__link"
              >
                <span @click="handleLinkClick">
                  {{ link.title }}
                </span>
              </T3Link>
            </template>
          </li>
        </ul>

        <div
          v-if="withLinkMedia"
          class="nav-desktop-subdrop__media-wrapper"
        >
          <T3Link
            class="nav-desktop-subdrop__media"
            :to="linkMedia?.properties?.linkData"
          >
            <UiImg
              use-image-size
              :image="linkMedia"
            />

            <div class="nav-desktop-subdrop__media-link">
              <span>
                {{ labels[lang as keyof typeof labels] }}

                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="currentColor"
                    d="M9.452 17.084l7.132-4.584-7.132-4.584v9.168zM12 22.704a9.946 9.946 0 01-3.983-.804 10.3 10.3 0 01-3.238-2.179 10.3 10.3 0 01-2.18-3.238 9.945 9.945 0 01-.802-3.983c0-1.415.268-2.743.803-3.983a10.3 10.3 0 012.18-3.238 10.3 10.3 0 013.237-2.18A9.945 9.945 0 0112 2.298c1.415 0 2.743.267 3.984.803 1.24.535 2.32 1.262 3.237 2.179a10.3 10.3 0 012.18 3.238 9.946 9.946 0 01.802 3.983 9.946 9.946 0 01-.803 3.983 10.3 10.3 0 01-2.179 3.238 10.3 10.3 0 01-3.237 2.18 9.946 9.946 0 01-3.984.802zm0-2.276c2.217 0 4.093-.767 5.627-2.3 1.534-1.535 2.301-3.41 2.301-5.628 0-2.217-.767-4.093-2.3-5.627-1.535-1.534-3.41-2.301-5.628-2.301-2.217 0-4.093.767-5.627 2.3-1.534 1.535-2.301 3.41-2.301 5.628 0 2.217.767 4.093 2.3 5.627 1.535 1.534 3.41 2.301 5.628 2.301z"
                  />
                </svg>
              </span>
            </div>
          </T3Link>
        </div>
      </div>
    </div>

    <div class="nav-desktop-subdrop__footer-wrapper">
      <div class="nav-desktop-subdrop__footer">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { T3Link } from '@t3headless/nuxt-typo3'
import { computed } from 'vue'

import SvgIconLinkArrow from '~ui/assets/icons/icon-link-arrow.svg'
import type { INavigation, INavigationWithMedia } from '~ui/types'

const props = defineProps<{ rootLink: INavigation | INavigationWithMedia, open: boolean, lang: string }>()

const withLinkMedia = computed(() => {
  if (!('media' in props.rootLink)) return
  return !!props.rootLink.media?.length
})

const linkMedia = computed(() => {
  if (!('media' in props.rootLink)) return
  return props.rootLink.media?.[0]
})

const withLinkOverview = computed(() => {
  return props.rootLink.header && props.rootLink.description && !withLinkMedia.value
})

const getLink = (link: INavigation): T3Link => {
  return { href: link.link, target: link.target }
}

const emit = defineEmits<{ (event: 'link-click'): void }>()
const handleLinkClick = () => emit('link-click')

const labels = {
  en: 'see more',
  pl: 'obejrzyj więcej',
  de: 'mehr sehen'
}
</script>

<style lang="scss">
.nav-desktop-subdrop {
  position: absolute;
  z-index: z-index(over);
  inset-inline: 0;
  top: var(--subdrop-offset, 0px);
  max-height: calc(100vh - var(--subdrop-offset, 0px));
  max-height: calc(100svh - var(--subdrop-offset, 0px));
  overflow-y: auto;

  border-block: 1px solid color(grey-250);
  visibility: hidden;
  opacity: 0;
  transition:
    visibility 0.2s,
    opacity 0.2s;

  &--open {
    visibility: visible;
    opacity: 1;
  }

  &__content,
  &__footer {
    @include frame-content-container(large);
  }

  &__content {
    background-color: color(white);
    padding-block: rem(24px);

    display: grid;
    gap: rem(24px);

    &--overview {
      grid-template-columns: 45fr 55fr;
    }

    &--media {
      grid-template-columns: 55fr 45fr;
    }
  }

  &__overview {
    color: color(grey-1000);

    &-header > span {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: rem(8px);
      font-size: rem(32px);
      font-weight: 600;
      margin-bottom: rem(16px);

      & svg {
        height: 32px;
        width: 32px;

        & path {
          transition: fill 0.2s;
        }
      }

      &::before {
        content: '';
        position: absolute;
        height: 2px;
        width: 100%;
        bottom: 0 !important;
        background-color: color(grey-550);
        transition: background-color 0.2s;
      }

      &:is(:hover, :active, :focus)::before {
        background-color: currentColor;
      }

      &:is(:hover, :active, :focus) svg path {
        fill: currentColor;
      }
    }

    &-description {
      color: color(grey-650);

      & > *:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: rem(20px);
    padding-left: rem($container-padding);

    & > *:first-child:last-child {
      grid-column: span 2;
    }
  }

  &__sublist {
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: rem(20px);

    &-title {
      display: inline-flex;
      align-items: center;
      color: color(grey-650);
      font-size: 14px;
      margin-bottom: rem(24px);

      &:is(:hover, :active, :focus) svg path {
        fill: currentColor;
      }

      & svg {
        height: 22px;
        width: 22px;

        & path {
          transition: fill 0.2s;
          fill: color(grey-450);
        }
      }
    }
  }

  &__media {
    display: grid;
    grid-template-areas: 'cell';
    margin-left: auto;
    width: fit-content;
    border-radius: em(4px);
    border: 1px solid color(grey-200);
    overflow: hidden;

    &-link {
      grid-area: cell;
      display: grid;
      place-content: center;
      opacity: 0;
      pointer-events: none;
      color: color(grey-800);
      background-color: rgba(color(primary), 30%);
      backdrop-filter: blur(2px);
      transition: opacity 0.2s;

      & > span {
        display: inline-flex;
        align-items: center;
        gap: rem(12px);
        background-color: color(white);
        border-radius: em(4px);
        padding: em(20px) em(24px);
        font-size: rem(18px);
        font-weight: 600;
        text-transform: uppercase;
      }

      & svg {
        color: color(primary);
      }
    }

    & > img {
      grid-area: cell;
      height: auto;
      max-width: 100%;
    }

    &:hover &-link {
      opacity: 1;
      pointer-events: all;
    }

    &:empty {
      display: none;
    }
  }

  &__link {
    color: color(grey-800);
    font-weight: 600;
    font-size: rem(18px);

    &.router-link-exact-active {
      text-decoration: 2px underline color(primary);
      text-underline-offset: 6px;
    }
  }

  &__footer {
    border-top: 1px solid color(grey-250);
    background-color: color(grey-100);

    &:empty {
      display: none;
    }
  }
}
</style>
